import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Upload, Search, LayoutGrid, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateSelector = ({ onSelect, onUpload }) => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const [templatesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/templates`),
          axios.get(`${API}/templates/categories`)
        ]);
        setTemplates(templatesRes.data);
        setCategories(['All', ...categoriesRes.data.categories]);
      } catch (error) {
        // Template fetch failed silently
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === 'All' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2 font-['Space_Grotesk']">
          Choose Your Template
        </h2>
        <p className="text-[#a1a1aa]">
          Select from popular templates or upload your own image
        </p>
      </div>

      {/* Upload Section */}
      <div className="mb-8">
        <input
          type="file"
          ref={fileInputRef}
          onChange={onUpload}
          accept="image/*"
          className="hidden"
          data-testid="image-upload-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-24 bg-[#18181b] border-2 border-dashed border-[#27272a] hover:border-[#bef264] hover:bg-[#1c1c1f] text-white transition-all"
          variant="ghost"
          data-testid="upload-btn"
        >
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-[#bef264]" />
            <span className="text-base font-medium">Upload Your Image</span>
            <span className="text-sm text-[#a1a1aa]">PNG, JPG, GIF up to 10MB</span>
          </div>
        </Button>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4 mb-8">
        <div className="flex-1 h-px bg-[#27272a]" />
        <span className="text-sm text-[#a1a1aa]">or choose a template</span>
        <div className="flex-1 h-px bg-[#27272a]" />
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a1a1aa]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="pl-10 bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b] focus:border-[#bef264]"
            data-testid="template-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              className={selectedCategory === category 
                ? 'bg-[#bef264] text-black hover:bg-[#a3e635]' 
                : 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]'
              }
              data-testid={`category-${category.toLowerCase()}`}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#bef264]" />
        </div>
      ) : (
        <ScrollArea className="h-[400px]">
          <div className="template-gallery">
            {filteredTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => onSelect(template)}
                className="template-card aspect-square rounded-xl overflow-hidden border border-[#27272a] hover:border-[#bef264] transition-all group relative"
                data-testid={`template-${template.id}`}
              >
                <img
                  src={template.url}
                  alt={template.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white text-sm font-medium truncate">{template.name}</p>
                    <p className="text-[#a1a1aa] text-xs">{template.category}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="empty-state">
              <LayoutGrid className="w-12 h-12 text-[#3f3f46] mb-4" />
              <p className="text-[#a1a1aa]">No templates found</p>
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
};

export default TemplateSelector;
